class Model {
  constructor() {
    this.fixed = samProOptions.xaFixed;
    this._corner = samProOptions.xaCorner;
    this._popup = samProOptions.xaPopup;
    this._background = samProOptions.xaBackground;
    this.storageSession = Model.storageAvailable('sessionStorage');
    this.storageLocal = Model.storageAvailable('localStorage');
  }

  get topLeft() {
    return this.fixed.topLeft
  }

  get middleLeft() {
    return this.fixed.middleLeft
  }

  get bottomLeft() {
    return this.fixed.bottomLeft
  }

  get topRight() {
    return this.fixed.topRight
  }

  get middleRight() {
    return this.fixed.middleRight
  }

  get bottomRight() {
    return this.fixed.bottomRight
  }

  get bottom() {
    return this.fixed.bottomMiddle
  }

  get popup() {
    return this._popup;
  }

  get corner() {
    return this._corner;
  }

  get backgroundLeft() {
    return this._background.left;
  }

  get backgroundRight() {
    return this._background.right;
  }

  get backgroundTarget() {
    return this._background.body;
  }

  static storageAvailable(type) {
    const storage = window[type];
    try {
      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    }
    catch (e) {
      return e instanceof DOMException && (
          // everything except Firefox
        e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage.length !== 0;
    }
  }

  getElementStorageData(key, data, repository = 'sessionStorage') {
    let storage = null;
    if (repository === 'sessionStorage' && this.storageSession) {
      storage = window.sessionStorage;
    }
    else if (repository === 'localStorage' && this.storageLocal) {
      storage = window.localStorage;
    }

    if (!!storage) {
      const value = JSON.parse(storage.getItem(key));
      if (!!value) return value;
      else {
        this.setElementStorageData(key, data, repository);
        return data;
      }
    }
    else return null;
  }

  setElementStorageData(key, data, repository = 'sessionStorage') {
    if (repository === 'sessionStorage' && this.storageSession) {
      window.sessionStorage.setItem(key, JSON.stringify(data));
    }
    else if (repository === 'localStorage' && this.storageLocal) {
      window.localStorage.setItem(key, JSON.stringify(data));
    }
    else return null;
  }
}

class View {
  constructor() {
    this.topLeft = null;
    this.middleLeft = null;
    this.bottomLeft = null;
    this.topRight = null;
    this.middleRight = null;
    this.bottomRight = null;
    this.bottom = null;

    this.popup = null;

    this.corner = null;

    this.backgroundLeft = null;
    this.backgroundRight = null;

    this.nodesEvent = new CustomEvent('xaNodesReady');
    this.cornerEvent = new CustomEvent('xaCornerReady');
    this.backgroundEvent = new CustomEvent('xaBackgroundReady');
  }

  assignCorner(corner) {
    if (corner.enabled) {
      this.corner = {
        node: document.querySelector('.sp-xa-corner'),
        data: corner,
      };
      this.corner.node.classList.add('shown');
      this.cornerEvent.data = this.corner;
      document.dispatchEvent(this.cornerEvent);
    }
  }

  assignBackground(background) {
    if (background.left.enabled) {
      const node = document.querySelector(background.body);
      node.classList.add('sp-xa-background');
      this.backgroundLeft = {node, data: background.left};
      if (background.right.enabled) {
        this.backgroundRight = {node, data: background.right};
      }
      this.backgroundEvent.data = {
        left: this.backgroundLeft,
        right: this.backgroundRight,
        node
      };
      document.dispatchEvent(this.backgroundEvent);
    }
  }

  assignNodes(fixed, popup) {
    if (fixed.topLeft.enabled) this.topLeft = {
      node: document.querySelector(`.${fixed.topLeft.el}`),
      data: fixed.topLeft,
    };
    if (fixed.middleLeft.enabled) this.middleLeft = {
      node: document.querySelector(`.${fixed.middleLeft.el}`),
      data: fixed.middleLeft,
    };
    if (fixed.bottomLeft.enabled) this.bottomLeft = {
      node: document.querySelector(`.${fixed.bottomLeft.el}`),
      data: fixed.bottomLeft,
    };
    if (fixed.topRight.enabled) this.topRight = {
      node: document.querySelector(`.${fixed.topRight.el}`),
      data: fixed.topRight,
    };
    if (fixed.middleRight.enabled) this.middleRight = {
      node: document.querySelector(`.${fixed.middleRight.el}`),
      data: fixed.middleRight,
    };
    if (fixed.bottomRight.enabled) this.bottomRight = {
      node: document.querySelector(`.${fixed.bottomRight.el}`),
      data: fixed.bottomRight,
    };
    if (fixed.bottom.enabled) this.bottom = {
      node: document.querySelector(`.${fixed.bottom.el}`),
      data: fixed.bottom,
    };
    if (popup.enabled) this.popup = {
      node: document.querySelector('.sp-popup-dialog'),
      data: popup,
    };

    this.nodesEvent.data = [
      this.topLeft,
      this.middleLeft,
      this.bottomLeft,
      this.topRight,
      this.middleRight,
      this.bottomRight,
      this.bottom,
      this.popup,
    ].reduce((a, c) => {
      if (!!c) a.push(c);
      return a;
    }, []);

    document.dispatchEvent(this.nodesEvent);
  }
}

class Controller {
  constructor(model, view) {
    this.model = model;
    this.view = view;
    this.track = samProOptions.jsStats;
    this.path = samProOptions.ajaxUrl;
    this.nonce = samProOptions.samProNonce;
    this.adLoadedEvent = !!samProOptions.adLoadedEvent;

    this.activeNodes = null;
    document.addEventListener('xaNodesReady', e => this.onNodesReady(e.data));
    document.addEventListener('xaCornerReady', e => this.onCornerReady(e.data));
    document.addEventListener('xaBackgroundReady', e => this.onBackgroundReady(e.data));
    window.addEventListener('scroll', () => this.onScroll());
  }

  init() {
    if (this.adLoadedEvent) {
      document.addEventListener('SamProAdsLoaded', () => this.renderElements());
    }
    else this.renderElements();
  }

  renderElements() {
    const fixed = {
      topLeft: (this.checkStorage(this.model.topLeft)) ? this.model.topLeft : {enabled: false},
      middleLeft: (this.checkStorage(this.model.middleLeft)) ? this.model.middleLeft : {enabled: false},
      bottomLeft: (this.checkStorage(this.model.bottomLeft)) ? this.model.bottomLeft : {enabled: false},
      topRight: (this.checkStorage(this.model.topRight)) ? this.model.topRight : {enabled: false},
      middleRight: (this.checkStorage(this.model.middleRight)) ? this.model.middleRight : {enabled: false},
      bottomRight: (this.checkStorage(this.model.bottomRight)) ? this.model.bottomRight : {enabled: false},
      bottom: (this.checkStorage(this.model.bottom)) ? this.model.bottom : {enabled: false},
    };

    this.view.assignBackground({
      left: this.model.backgroundLeft,
      right: this.model.backgroundRight,
      body: this.model.backgroundTarget,
    });
    this.view.assignCorner(this.model.corner);
    this.view.assignNodes(fixed, ((this.checkStorage(this.model.popup)) ? this.model.popup : {enabled: false}));
  }

  checkStorage({enabled = false, view = 'always', times = 1, repository = 'sessionStorage', period = 1440, el}) {
    if (!enabled) return false;
    if (view === 'always') return true;

    let key = '';
    switch (el) {
      case 'sp-fixed-top-left':
        key = 'xaTopLeft';
        break;
      case 'sp-fixed-middle-left':
        key = 'xaMiddleLeft';
        break;
      case 'sp-fixed-bottom-left':
        key = 'xaBottomLeft';
        break;
      case 'sp-fixed-top-right':
        key = 'xaTopRight';
        break;
      case 'sp-fixed-middle-right':
        key = 'xaMiddleRight';
        break;
      case 'sp-fixed-bottom-right':
        key = 'xaBottomRight';
        break;
      case 'sp-fixed-bottom-container':
        key = 'xaBottom';
        break;
      default:
        key = 'xaPopup';
    }

    const data = this.model.getElementStorageData(key, {
      left: times,
      expire: Date.now() + period * 60000,
    }, repository);

    if (repository === 'sessionStorage') {
      this.model.setElementStorageData(key, {
        left: Math.max(0, data.left - 1),
        expire: Date.now() + period * 60000,
      }, repository);
      return (0 < data.left);
    }
    else {
      const expired = (data.expire < Date.now());
      this.model.setElementStorageData(key, {
        left: (expired) ? Math.max(0, times - 1) : Math.max(0, data.left - 1),
        expire: (expired) ? Date.now() + period * 60000 : data.expire,
      }, repository);
      return ((0 < data.left && !expired) || expired);
    }
  }

  onNodesReady(data) {
    if (!!data) {
      this.activeNodes = data;
      this.activeNodes.forEach((el, idx) => {
        switch (el.data.event) {
          case 'immediately':
            this.activeNodes[idx].data.position = null;
            if (Number(el.data.delay).valueOf() > 0) {
              window.setTimeout(() => el.node.classList.add('shown'), Number(el.data.delay).valueOf());
            }
            else el.node.classList.add('shown');
            break;
          case 'scroll':
            this.activeNodes[idx].data.position = Number(el.data.offset).valueOf();
            break;
          case 'middle':
            this.activeNodes[idx].data.position = document.body.scrollHeight / 2 + Number(el.data.offset).valueOf();
            break;
          case 'target':
            const target = document.querySelector(el.data.element);
            this.activeNodes[idx].data.position = target.offsetTop + Number(el.data.offset).valueOf();
            break;
          case 'targetEnd':
            const targetEnd = document.querySelector(el.data.element);
            this.activeNodes[idx].data.position =
              targetEnd.offsetTop + targetEnd.offsetHeight + Number(el.data.offset).valueOf();
            break;
          default:
            this.activeNodes[idx].data.position = null;
        }
        this.setListener(el.node);
      });
    }
  }

  onCornerReady(data) {
    if (!!data && this.track) {
      const cornerAnchor = data.node.querySelector('.corner-link');
      cornerAnchor.addEventListener('click', e => {
        e.preventDefault();
        const body = new URLSearchParams();
        body.append('action', 'sam_click');
        body.append('aid', data.data.aid);
        body.append('pid', data.data.pid);
        body.append('sam_pro_nonce', this.nonce);
        fetch(this.path, {method: 'POST', body});
        window.open(data.data.link);
      });
      const params = new URLSearchParams();
      params.append('action', 'sam_hits');
      params.append('hits[0][pid]', data.data.pid);
      params.append('hits[0][aid]', data.data.aid);
      params.append('sam_pro_nonce', this.nonce);
      fetch(this.path, {method: 'POST', body: params});
    }
  }

  onBackgroundReady(data) {
    const node = data.node;
    const left = data.left;
    const right = data.right;

    document.addEventListener('mousemove', e => {
      const target = e.target;
      if (!!target.classList) {
        if (target.classList.contains('sp-xa-background')) {
          target.style.cursor = 'pointer';
        }
        else target.style.cursor = 'initial';
      }
    });

    node.addEventListener('click', e => {
      const documentWidth = document.body.clientWidth;
      const target = e.target;
      if (target.classList.contains('sp-xa-background')) {
        if (right.data.enabled && e.pageX > documentWidth / 2) {
          const params = new URLSearchParams();
          params.append('action', 'sam_click');
          params.append('aid', right.data.aid);
          params.append('pid', right.data.pid);
          params.append('sam_pro_nonce', this.nonce);
          fetch(this.path, {method: 'POST', body: params});
          window.open(right.data.link);
        }
        else {
          const params = new URLSearchParams();
          params.append('action', 'sam_click');
          params.append('aid', left.data.aid);
          params.append('pid', left.data.pid);
          params.append('sam_pro_nonce', this.nonce);
          fetch(this.path, {method: 'POST', body: params});
          window.open(left.data.link);
        }
      }
    });

    if (right.data.enabled) {
      const params = new URLSearchParams();
      params.append('action', 'sam_hits');
      params.append('hits[0][aid]', left.data.aid);
      params.append('hits[0][pid]', left.data.pid);
      params.append('hits[1][aid]', right.data.aid);
      params.append('hits[1][pid]', right.data.pid);
      params.append('sam_pro_nonce', this.nonce);
      fetch(this.path, {method: 'POST', body: params});
    }
    else {
      const params = new URLSearchParams();
      params.append('action', 'sam_hits');
      params.append('hits[0][aid]', left.data.aid);
      params.append('hits[0][pid]', left.data.pid);
      params.append('sam_pro_nonce', this.nonce);
      fetch(this.path, {method: 'POST', body: params});
    }
  }

  onScroll() {
    if (!!this.activeNodes) {
      const pos = window.scrollY;
      this.activeNodes.forEach(el => {
        if (el.data.position !== null && pos > el.data.position) {
          if (!el.node.classList.contains('shown')) {
            if (Number(el.data.delay).valueOf() > 0) {
              window.setTimeout(() => el.node.classList.add('shown'), Number(el.data.delay).valueOf());
            }
            else el.node.classList.add('shown');
          }
        }
      });
    }
  }

  setListener(node) {
    const button = node.querySelector('.sp-button-close');
    if (!!button) button.addEventListener('click', () => {
      const idx = this.activeNodes.findIndex(n => n.node === node);
      node.classList.add('hided');
      node.classList.remove('shown');
      this.activeNodes.splice(idx, 1);
    });
  }
}

(function () {
  document.addEventListener('DOMContentLoaded', () => {
    const model = new Model();
    const view = new View();
    const controller = new Controller(model, view);
    controller.init();
  });
})();
